/**
 * Bind the fixed props of a shared card to one usage — typically its
 * registered `componentId`, its base `title` and, for `EntityInfoCard`, its
 * `fields` config — and return the narrower component a domain registers.
 *
 * ```ts
 * const ContractDocumentsCard = bindCardProps(DocumentsByCategoryCard, {
 *   componentId: 'extranet.contractDocumentsCard',
 *   title: 'Documents',
 * });
 * registerOnce({ id: 'extranet.contractDocumentsCard', component: ContractDocumentsCard, … });
 * export const ContractDocumentsCardConfigurable = withComponentRules(
 *   ContractDocumentsCard,
 *   'extranet.contractDocumentsCard',
 * );
 * ```
 *
 * The bound props are config, not data: they never appear in the usage's
 * `propsSchema`, so a rule cannot `setProp` them; the data props (`rows`,
 * `values`, `loading`, `error`, …) and the injected `_layoutOverrides` flow
 * through unchanged.
 */

import type { ComponentType } from 'react';

export function bindCardProps<P extends object, K extends keyof P>(
  Component: ComponentType<P>,
  bound: Pick<P, K>,
  displayName?: string,
): ComponentType<Omit<P, K>> {
  function BoundCard(props: Omit<P, K>) {
    const merged = { ...props, ...bound } as P;
    return <Component {...merged} />;
  }
  const baseName = Component.displayName ?? (Component.name !== '' ? Component.name : 'Card');
  const componentId = (bound as { componentId?: unknown }).componentId;
  BoundCard.displayName =
    displayName ?? (typeof componentId === 'string' ? `${baseName}(${componentId})` : baseName);
  return BoundCard;
}
